import { temporaryAccessAPI } from '../api/client';
import { signRequestWithKey } from '../crypto/signing';
import { verifyEd25519 } from '../crypto/ed25519Provider';
import { encryptGroupKeyEnvelope } from '../groupChats/crypto';
import { encryptDirectKeyEnvelope } from '../directChats/crypto';
import { base64ToBytes } from '../utils/encoding';
import type { Identity, Device } from '../store/authStore';
import type { PublicKey } from '@shared/types';

type ApprovalAttestation = {
  payload: string;
  signature: string;
  approvingDevicePublicKey: string;
};

type AttestationPayload = {
  action: string;
  tempDeviceId: string;
  encryptionPublicKeyValue: string | null;
  expiresAt: string;
  chatGrants: Array<{ chatId: string; chatType: 'group' | 'direct' }>;
};

async function verifyApprovalAttestation(
  device: { deviceId: string; encryptionPublicKey: { algorithm: string; value: string } | null },
  attestation: ApprovalAttestation,
  expectedApprovingDevicePublicKey: string,
  chatId: string,
  chatType: 'group' | 'direct'
): Promise<boolean> {
  // Signer must be our own device
  if (attestation.approvingDevicePublicKey !== expectedApprovingDevicePublicKey) return false;

  const payloadBytes = new TextEncoder().encode(attestation.payload);
  const sigBytes = base64ToBytes(attestation.signature);
  const pubKeyBytes = base64ToBytes(attestation.approvingDevicePublicKey);

  const valid = await verifyEd25519(payloadBytes, sigBytes, pubKeyBytes);
  if (!valid) return false;

  let parsed: AttestationPayload;
  try {
    parsed = JSON.parse(attestation.payload) as AttestationPayload;
  } catch {
    return false;
  }

  if (parsed.action !== 'temp-device-approval') return false;
  if (parsed.tempDeviceId !== device.deviceId) return false;
  if (parsed.encryptionPublicKeyValue !== (device.encryptionPublicKey?.value ?? null)) return false;
  if (!Array.isArray(parsed.chatGrants) || !parsed.chatGrants.some((grant) => grant.chatType === chatType && grant.chatId === chatId)) return false;
  if (!Date.parse(parsed.expiresAt) || Date.parse(parsed.expiresAt) <= Date.now()) return false;

  return true;
}

export async function publishTempDeviceEnvelopes(params: {
  chatId: string;
  epoch: number;
  rawKey: Uint8Array;
  localIdentity: Identity;
  localDevice: Device;
}): Promise<void> {
  const { chatId, epoch, rawKey, localIdentity, localDevice } = params;
  const serverUrl = localIdentity.serverUrl;

  const listSigned = await signRequestWithKey(
    'temporary-access:chat-keys:approved-devices',
    { chatId, chatType: 'group' as const },
    localDevice.deviceId,
    localDevice.signingKey
  );

  const response = await temporaryAccessAPI.fetchApprovedTempDevicesForChat(listSigned, serverUrl);
  if (response.status !== 'ok' || !response.result) return;

  const { devices } = response.result;
  if (devices.length === 0) return;

  const envelopes: Array<{
    temporaryDeviceId: string;
    envelopeCiphertext: string;
    publisherEncPublicKeyAlgo: string;
    publisherEncPublicKeyValue: string;
  }> = [];

  for (const device of devices) {
    if (!device.encryptionPublicKey) continue;

    // Reject devices without attestation or with invalid attestation.
    // This prevents a compromised server from injecting foreign devices.
    if (!device.approvalAttestation) continue;
    const attValid = await verifyApprovalAttestation(device, device.approvalAttestation, localDevice.publicKey.value, chatId, 'group');
    if (!attValid) continue;

    const envelopeCiphertext = await encryptGroupKeyEnvelope({
      rawKey,
      publisherIdentityId: localIdentity.identityId,
      publisherEcdhKey: localIdentity.ecdhKey,
      publisherEcdhScalar: localIdentity.ecdhScalar,
      recipientPublicKey: device.encryptionPublicKey as PublicKey
    });
    envelopes.push({
      temporaryDeviceId: device.deviceId,
      envelopeCiphertext,
      publisherEncPublicKeyAlgo: localIdentity.publicKey.algorithm,
      publisherEncPublicKeyValue: localIdentity.publicKey.value,
    });
  }

  if (envelopes.length === 0) return;

  const updateSigned = await signRequestWithKey(
    'temporary-access:chat-keys:update',
    { chatId, chatType: 'group' as const, epoch, envelopes },
    localDevice.deviceId,
    localDevice.signingKey
  );

  await temporaryAccessAPI.updateTempDeviceChatKeys(updateSigned, serverUrl);
}

export async function publishTempDeviceDirectEnvelopes(params: {
  directChatId: string;
  epoch: number;
  rawKey: Uint8Array;
  localIdentity: Pick<Identity, 'identityId' | 'serverUrl' | 'publicKey' | 'ecdhKey' | 'ecdhScalar'>;
  localDevice: Device;
}): Promise<void> {
  const { directChatId, epoch, rawKey, localIdentity, localDevice } = params;
  const listSigned = await signRequestWithKey(
    'temporary-access:chat-keys:approved-devices',
    { chatId: directChatId, chatType: 'direct' as const },
    localDevice.deviceId,
    localDevice.signingKey
  );
  const response = await temporaryAccessAPI.fetchApprovedTempDevicesForChat(listSigned, localIdentity.serverUrl);
  if (response.status !== 'ok' || !response.result) return;

  const envelopes: Array<{
    temporaryDeviceId: string;
    envelopeCiphertext: string;
    publisherEncPublicKeyAlgo: string;
    publisherEncPublicKeyValue: string;
  }> = [];
  for (const device of response.result.devices) {
    if (!device.encryptionPublicKey || !device.approvalAttestation) continue;
    const attValid = await verifyApprovalAttestation(
      device,
      device.approvalAttestation,
      localDevice.publicKey.value,
      directChatId,
      'direct'
    );
    if (!attValid) continue;
    envelopes.push({
      temporaryDeviceId: device.deviceId,
      envelopeCiphertext: await encryptDirectKeyEnvelope({
        rawKey,
        publisherIdentityId: localIdentity.identityId,
        publisherEcdhKey: localIdentity.ecdhKey,
        publisherEcdhScalar: localIdentity.ecdhScalar,
        recipientPublicKey: device.encryptionPublicKey as PublicKey
      }),
      publisherEncPublicKeyAlgo: localIdentity.publicKey.algorithm,
      publisherEncPublicKeyValue: localIdentity.publicKey.value
    });
  }
  if (envelopes.length === 0) return;
  const updateSigned = await signRequestWithKey(
    'temporary-access:chat-keys:update',
    { chatId: directChatId, chatType: 'direct' as const, epoch, envelopes },
    localDevice.deviceId,
    localDevice.signingKey
  );
  await temporaryAccessAPI.updateTempDeviceChatKeys(updateSigned, localIdentity.serverUrl);
}
